import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, CardMedia, CircularProgress,
  IconButton, MobileStepper, TextField,
} from "@mui/material";
import {
  AccessAlarmsOutlined, AddOutlined, CalendarMonthOutlined, Delete,
  KeyboardArrowLeft, KeyboardArrowRight,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import "../publicaciones-home/ads.css";
import { MEDIA_MOBILE } from "../publicaciones-home/CardPublicacion";
import { fechaEventoTexto, horaInicioTexto } from "../../utils/evento";
import { subirArchivos, descartarArchivo, MAX_TAMANIO_TEXTO } from "../../utils/media";
import { LIMITES, publicacionVacia } from "../../domain/publicacion/schema";
import { toPayload } from "../../domain/publicacion/payload";
import { validarPublicacion, hayErrores } from "../../domain/publicacion/validacion";

const ANCHO_MEDIA = 370;
const ALTO_MEDIA_COMPACTA = 150; // como .media-publicacion

const cajaVaciaSx = {
  width: ANCHO_MEDIA,
  maxWidth: "100%",
  height: "100%",
  minHeight: 0,
  backgroundColor: "#f5f5f5",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  border: "2px dashed #ccc",
  borderRadius: "8px",
};

const campoSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#5656FF" },
  "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#5656FF" },
};

const botonFlotante = (lado) => ({
  position: "absolute",
  top: 8,
  [lado]: 8,
  backgroundColor: "rgba(255,255,255,0.8)",
});

const EditorPublicacion = ({
  evento,
  eventoId,
  valoresIniciales,
  esNueva = true,
  onSubmit,
  textoSubmit = "Guardar publicación",
  periodoArriba = false,
  accesorioPeriodo = null, // va al lado del título del período
  children,
}) => {
  const [form, setForm] = useState(() => valoresIniciales ?? publicacionVacia());
  const [errores, setErrores] = useState({});
  const [indice, setIndice] = useState(0);
  const [subiendo, setSubiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);
  const compacta = useMediaQuery({ query: MEDIA_MOBILE });

  const medidasMedia = compacta
    ? { width: "100%", height: ALTO_MEDIA_COMPACTA }
    : { width: ANCHO_MEDIA, maxWidth: "100%", height: "100%" };
  const claseMedia = compacta ? "media-publicacion" : "media-publicacion-indiv";

  useEffect(() => {
    if (valoresIniciales) setForm(valoresIniciales);
  }, [valoresIniciales]);

  const total = form.media.length;
  const actual = form.media[Math.min(indice, Math.max(total - 1, 0))];

  const setCampo = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    if (errores[campo]) {
      setErrores((prev) => {
        const resto = { ...prev };
        delete resto[campo];
        return resto;
      });
    }
  };

  const subir = async (e) => {
    // copiar antes del reset: e.target.files es una lista viva y se vacía
    const archivos = Array.from(e.target.files ?? []);
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!archivos.length) return;

    setSubiendo(true);
    try {
      const { items, rechazados } = await subirArchivos(archivos);
      rechazados.forEach((n) => toast.error(`"${n}" supera el límite de ${MAX_TAMANIO_TEXTO}.`));
      if (items.length) {
        setCampo("media", [...form.media, ...items]);
        setIndice(form.media.length);
      }
    } catch (error) {
      toast.error(error.message || "No se pudieron subir los archivos.");
    } finally {
      setSubiendo(false);
    }
  };

  const quitarActual = async () => {
    const i = Math.min(indice, total - 1);
    const item = form.media[i];
    setCampo("media", form.media.filter((_, idx) => idx !== i));
    setIndice((prev) => Math.max(0, Math.min(prev, total - 2)));
    await descartarArchivo(item?.public_id);
  };

  const mover = (paso) => setIndice((i) => (i + paso + total) % total);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const encontrados = validarPublicacion(form, { esNueva });
    setErrores(encontrados);
    if (hayErrores(encontrados)) return;

    setEnviando(true);
    try {
      await onSubmit(toPayload(form, eventoId));
    } finally {
      setEnviando(false);
    }
  };

  const renderMedia = () => {
    if (subiendo) {
      return (
        <Box sx={{ ...cajaVaciaSx, ...medidasMedia }}>
          <CircularProgress size={28} />
          <p style={{ color: "#666", margin: "10px 0 0" }}>Subiendo archivos...</p>
        </Box>
      );
    }

    if (!total) {
      return (
        <Box
          sx={{ ...cajaVaciaSx, ...medidasMedia, cursor: "pointer", "&:hover": { backgroundColor: "#f0f0f0" } }}
          onClick={() => inputRef.current?.click()}
        >
          <AddOutlined sx={{ fontSize: "3rem", color: "#666", mb: 1 }} />
          <p style={{ color: "#666", margin: 0 }}>Hacé clic para agregar imágenes o videos</p>
        </Box>
      );
    }

    return (
      <div style={{ position: "relative", height: "100%" }}>
        {actual?.type === "video" ? (
          <CardMedia component="video" src={actual.url} controls sx={medidasMedia} className={claseMedia} />
        ) : (
          <CardMedia component="img" src={actual?.url} alt="Imagen de la publicación" sx={{ ...medidasMedia, objectFit: "cover" }} className={claseMedia} />
        )}

        {total > 1 && (
          <>
            <IconButton style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", color: "#5656FF" }} onClick={() => mover(-1)} aria-label="Anterior">
              <KeyboardArrowLeft />
            </IconButton>
            <IconButton style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", color: "#5656FF" }} onClick={() => mover(1)} aria-label="Siguiente">
              <KeyboardArrowRight />
            </IconButton>
            <MobileStepper
              steps={total}
              position="static"
              activeStep={Math.min(indice, total - 1)}
              style={{ background: "transparent", position: "absolute", bottom: 0, width: "100%" }}
              nextButton={<div />}
              backButton={<div />}
            />
          </>
        )}

        <IconButton style={{ ...botonFlotante("right"), color: "#ff4757" }} onClick={quitarActual} size="small" aria-label="Quitar">
          <Delete />
        </IconButton>
        <IconButton style={{ ...botonFlotante("left"), color: "#5656FF" }} onClick={() => inputRef.current?.click()} size="small" aria-label="Agregar">
          <AddOutlined />
        </IconButton>
      </div>
    );
  };

  const campoTitulo = (estilo) => (
    <TextField
      fullWidth
      size="small"
      value={form.titulo}
      onChange={(e) => setCampo("titulo", e.target.value)}
      placeholder="Título de la publicación"
      inputProps={{ maxLength: LIMITES.TITULO_MAX, style: { fontWeight: 600, ...estilo } }}
      sx={campoSx}
    />
  );

  const fechaHora = (
    <div className="fechaHora-publicacion mb-1">
      <div className="fecha-publicacion d-flex align-items-center">
        <CalendarMonthOutlined fontSize="small" sx={{ mr: "5px" }} />
        <small>{fechaEventoTexto(evento, "D [de] MMM", "Fecha del evento")}</small>
      </div>
      <div className="d-flex align-items-center">
        <AccessAlarmsOutlined fontSize="small" sx={{ mr: "5px" }} />
        <small>{horaInicioTexto(evento, "LT", "Hora del evento")}</small>
      </div>
    </div>
  );

  const campoDescripcion = (
    <TextField
      fullWidth
      multiline
      minRows={2}
      maxRows={3}
      size="small"
      value={form.descripcion}
      onChange={(e) => setCampo("descripcion", e.target.value)}
      placeholder="Descripción de la publicación"
      inputProps={{ maxLength: LIMITES.DESCRIPCION_MAX }}
      helperText={`${form.descripcion.length}/${LIMITES.DESCRIPCION_MAX}`}
      FormHelperTextProps={{ sx: { textAlign: "right", mr: 0, mt: 0.25 } }}
      sx={campoSx}
    />
  );

  const tarjetaCompacta = (
    <div className="mt-4 contenedor-card-normal d-flex justify-content-center">
      <Card className="card-publicacion" sx={{ boxShadow: "none", cursor: "default", "&:hover": { transform: "none !important" } }}>
        <div style={{ position: "relative" }}>{renderMedia()}</div>
        <CardContent className="contenido-publicacion d-flex flex-column gap-2">
          {campoTitulo({ fontSize: "18px", fontFamily: "Inter, sans-serif" })}
          {fechaHora}
          {campoDescripcion}
        </CardContent>
      </Card>
    </div>
  );

  const seccionTarjeta = compacta ? tarjetaCompacta : (
    <>
      <div className="mt-4 contenedor-card" style={{ display: "flex", justifyContent: "center" }}>
        <Card className="card-publicacion-indiv" sx={{ boxShadow: "none", borderRadius: "10px", padding: "5px", cursor: "default" }}>
          <div style={{ position: "relative", height: "100%", maxHeight: "300px" }}>{renderMedia()}</div>

          <CardContent className="contenido-publicacion-indiv">
            {campoTitulo({ fontSize: "18px", fontFamily: "Bricolage Grotesque" })}

            {fechaHora}
            <p className="mb-0">Lugar: {evento?.direccion || "Dirección del evento"}</p>

            {campoDescripcion}

            <div className="d-flex justify-content-end">
              <Button tabIndex={-1} sx={{ backgroundColor: "#5656FF", color: "white", borderRadius: "8px", "&:hover": { backgroundColor: "#3939AA" } }}>
                Ver más
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );

  const seccionPeriodo = (
    <>
      <div className={periodoArriba ? "mt-2" : "mt-4"}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
          <p className="subtitulo-seccion-evento-expanded mb-0">
            Período de la publicación <span className="text-danger">*</span>
          </p>
          {accesorioPeriodo}
        </div>
        <p className="descrip-seccion-evento-expanded mb-2">
          La publicación se muestra en el inicio entre estas dos fechas (hora de Argentina).
        </p>
        <div className={compacta ? "d-flex flex-column gap-3" : "d-flex gap-3"}>
          <TextField
            fullWidth
            label="Desde"
            type="datetime-local"
            value={form.desde}
            onChange={(e) => setCampo("desde", e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            fullWidth
            label="Hasta"
            type="datetime-local"
            value={form.hasta}
            onChange={(e) => setCampo("hasta", e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </div>
      </div>
    </>
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      <input ref={inputRef} type="file" accept="image/*,video/*" multiple hidden onChange={subir} />

      {periodoArriba ? (
        <>
          {seccionPeriodo}
          {seccionTarjeta}
        </>
      ) : (
        <>
          {seccionTarjeta}
          {seccionPeriodo}
        </>
      )}

      {hayErrores(errores) && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {Object.values(errores).map((msg) => <div key={msg}>{msg}</div>)}
        </Alert>
      )}

      <div className="d-flex justify-content-end gap-2 flex-wrap mt-3">
        {children}
        <Button type="submit" variant="contained" disabled={enviando || subiendo} sx={{ backgroundColor: "#5656FF", "&:hover": { backgroundColor: "#4545ee" } }}>
          {enviando && <CircularProgress size={18} sx={{ mr: 1, color: "white" }} />}
          {enviando ? "Guardando..." : textoSubmit}
        </Button>
      </div>
    </form>
  );
};

export default EditorPublicacion;
